
import "./CreateSessionModal.scss";
import history from "../../../history";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, isEmpty } from "lodash";
import moment from "moment";
import Datetime from 'react-datetime';
import PropTypes from 'prop-types';
import clsx from "clsx";
import "react-datetime/css/react-datetime.css";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import useDatetimeKeyboardNav from "../../../hooks/useDatetimeKeyboardNav";
import { PopupModal } from "../../UI/Modal/Modal";
import PublicVariables from "../../../constants/PublicVariables";
import { checkRegexPattern, filterParams, handleErrorMsgToastVisiblity, parseURLParams, replaceSpecialChar } from "../../../shared/utility";
import { DatePicker, Timepicker, renderDay, renderMonth, renderYear } from "./DateTimePicker";
import { createSession, createSessionReset, getOwnCPList, editScheduleSession, editSessionScheduleReset } from "../../../actions/sessionListAction";
import { ONLY_NUMERIC_REGEX, RouteName, LONG_DATEPICKER_FORMAT, TIME_FORMAT, LOCAL_STORAGE_KEYS, CREATE_SESSION_FROM } from "../../../constants/Constants";
import { disablePrviousDate, generateScheduleSessionPayload, handleOnDateTimeChange, initializeDateAndTimePicker, initializeTimeZone } from "./utils";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";
import { announceResultCount } from "../../UI/LiveRegion";
import storage from "../../../services/storage";
import HostDropDown from "./HostDropDown";
import useMenuKeyboardNav from "../../../hooks/useMenuKeyboardNav";
import InfoToggletip from "../../UI/Tooltip/InfoToggletip";


const CreateSessionModal = (props) => {
    const { closeModal, inviteBuddyData = {}, isForEdit = false, sessionDetails = {}, selectedContact = null } = props
    const dispatch = useDispatch()
    const {
        getOwnCPListLoading,
        getOwnCPListData,
        createSessionsLoading,
        createSessionsData,
        createSessionsError,
        putScheduleSessionsLoading,
        putScheduleSessionsData,
        putScheduleSessionsError,
    } = useSelector((state) => state.sessionList);
    const { language } = useSelector((state) => state)

    const [ownCPList, setOwnCPList] = useState([])
    const [selectedColabrationProfile, setSelectedColabrationProfile] = useState({})
    const [sessionTitle, setSessionTitle] = useState('')
    const [error, setError] = useState(false)
    const [isScheduleSession, setIsScheduleSession] = useState(isForEdit)
    const [timeZonesList, setTimeZonesList] = useState([])
    const timeZoneAnnounceTimerRef = useRef(null)
    const { triggerRef: timeZoneTriggerRef, menuRef: timeZoneMenuRef } = useMenuKeyboardNav({ itemSelector: '.dropdown-item', listbox: true })
    const [selectedTimeZone, setSelectedTimeZone] = useState({})
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [startTime, setStartTime] = useState('')
    const [endTime, setEndTime] = useState('')
    const [durationOfSession, setDurationOfSession] = useState('30m')
    const [isDateTimeValid, setIsDateTimeValid] = useState(true)
    const [isDateDiffLimitExceeded, setIsDateDiffLimitExceeded] = useState(false)
    const [localisedValues, setLocalisedValues] = useState({});
    const ariaLangId = useSelector((state) => state.language?.selectedlanguage?.id);
    const ariaLocalised = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];
    // KBD-P14 (A11Y-14): wraps `react-datetime`'s calendar with ArrowLeft/Right/
    // Up/Down / Home / End / PageUp/Down / Enter / Escape keyboard nav —
    // library itself has none. See src/hooks/useDatetimeKeyboardNav.js.
    useDatetimeKeyboardNav({
        previous: ariaLocalised.previous_month,
        next: ariaLocalised.next_month,
        previousYear: ariaLocalised.previous_year,
        nextYear: ariaLocalised.next_year,
        previousYears: ariaLocalised.previous_years,
        nextYears: ariaLocalised.next_years,
        counters: [ariaLocalised.hours_label, ariaLocalised.minutes_label, ariaLocalised.am_pm_label],
    });
    const [isDateTimeFieldFocused, setIsDateTimeFieldFocused] = useState(!isForEdit)
    // WCAG 4.1.2: react-datetime exposes no open state to assistive tech, so
    // track which of the four pickers is open and report it on that picker's
    // calendar/clock button. Only one can be open at a time.
    const [openDatetimePicker, setOpenDatetimePicker] = useState(null)
    const [hostSelectedId, setHostSelectedId] = useState(null);
    const [isInviteSent, setIsInviteSent] = useState(true) //Default send invite should checked
    const startDateRef = useRef()
    const startTimeRef = useRef()
    const endDateRef = useRef()
    const endTimeRef = useRef()
    const referenceInputRef = useRef()
    const dataForSession = useMemo(() => { return parseURLParams(storage.get(LOCAL_STORAGE_KEYS.DATA_FOR_SESSION) || '') }, []);

    // `onOpen` / `onClose` are react-datetime's only window into its popup
    // state (typings: node_modules/react-datetime/typings/DateTime.d.ts).
    const datetimeOpenProps = (key) => ({
        onOpen: () => setOpenDatetimePicker(key),
        onClose: () => setOpenDatetimePicker((current) => (current === key ? null : current)),
    });

    // Spread onto `inputProps`. `aria-haspopup` is a global ARIA attribute, so
    // it is valid on the text input and tells AT a popup exists at all.
    //
    // `aria-expanded` deliberately does NOT go here: an <input type="text"> has
    // role `textbox`, which does not allow it (axe's role table lists only
    // aria-activedescendant / -autocomplete / -multiline / -placeholder /
    // -readonly / -required). Putting it on the input means screen readers
    // ignore it *and* axe flags `aria-allowed-attr`. It lives on the calendar
    // button instead, via `isPickerOpen` — role `button` does allow it.
    // The alternative is `role="combobox"` on the input, but axe lists
    // `aria-controls` as a *required* attr of combobox, which would drag the
    // popup-id stamping back in.
    const datetimeInputAria = (key, fieldLabel) => {
        const showError = isDateTimeFieldFocused && (!isDateTimeValid || isDateDiffLimitExceeded);
        return {
            'aria-haspopup': 'dialog',
            isPickerOpen: openDatetimePicker === key,
            fieldLabel,
            'aria-invalid': showError || undefined,
            'aria-describedby': showError ? 'session-datetime-error' : undefined,
        };
    };

    const getAllTimeZones = useMemo(() => {
        const timeZones = moment.tz.names().map(tz => ({
            timezoneWithOffset: " (GMT" + moment.tz(tz).format('Z') + ")" + tz,
            timezone: tz
        }))
        return timeZones
    }, [])

    useEffect(() => () => clearTimeout(timeZoneAnnounceTimerRef.current), [])

    useEffect(() => {
        // Setting Session start , end time & timeZone for edit session schedule
        if (isForEdit && !isEmpty(sessionDetails)) {
            const selectedProfile = {
                name: sessionDetails.session.collaborationProfileName,
                collaborationProfileId: sessionDetails.session.collaborationProfileId
            }
            setSelectedColabrationProfile(selectedProfile)
            setOwnCPList([selectedProfile])
            const seesion_title = isEmpty(sessionDetails.session.reference) ? 'N/A' : sessionDetails.session.reference
            setSessionTitle(seesion_title)
        }
    }, [sessionDetails])

    useEffect(() => {
        const curLang = language.selectedlanguage;
        setLocalisedValues(Localize[curLang.id]);
    }, [language.selectedlanguage]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key !== 'Escape') return;
            if (!document.querySelector('.rdt.rdtOpen')) return;
            e.stopPropagation();
            e.preventDefault();
            document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        };
        document.addEventListener('keydown', handleEsc, true);
        return () => document.removeEventListener('keydown', handleEsc, true);
    }, []);

    useEffect(() => {
        initializeTimeZone({ setTimeZonesList, setSelectedTimeZone, getAllTimeZones, isForEdit, sessionDetails })
        initializeDateAndTimePicker({ setStartDate, setEndDate, setStartTime, setEndTime, isForEdit, sessionDetails })
        /* The below code is checking if the result of the `getOwnCPListData` function is empty. If it
        is empty & isFor not edit than, it dispatches the `getOwnCPList` action. */
        if (isEmpty(get(getOwnCPListData, 'items', [])) && !isForEdit) {
            dispatch(getOwnCPList());
        }

    }, []);

    useEffect(() => {
        if (isForEdit) return
        if (getOwnCPListLoading) return
        const cpListData = get(getOwnCPListData, 'items', [])
        setOwnCPList(cpListData)
        if (dataForSession.callexperience) {
            const callExp = cpListData.find(obj => obj.name === dataForSession.callexperience);
            setSelectedColabrationProfile(callExp ?? cpListData[0]);
        } else {
            setSelectedColabrationProfile(cpListData[0])
        }
        setSessionTitle(dataForSession.ref ?? '')
    }, [getOwnCPListData])

    useEffect(() => {
        if (createSessionsLoading) return
        if (createSessionsData) {
            handleCloseModal()
            history.push({
                pathname: RouteName.SEND_CODE,
                state: {
                    buddyData: inviteBuddyData,
                    selectedContact: selectedContact
                }
            })
        }
        else if (createSessionsError) {
            // Resetting create Sessions Store data if any error accured during Creating Session
            dispatch(createSessionReset())
            handleErrorMsgToastVisiblity(createSessionsError.message)
        }
    }, [createSessionsLoading, createSessionsData, createSessionsError, selectedContact])

    useEffect(() => {
        if (putScheduleSessionsLoading) return
        if (putScheduleSessionsData) {
            handleCloseModal()
            history.push({ pathname: RouteName.SEND_CODE,  state: {isFromChangeSchedule : true}})
            dispatch(editSessionScheduleReset())
        }
        else if (putScheduleSessionsError) {
            // Resetting Edit Sessions Store data if any error accured during Editing Session 
            dispatch(editSessionScheduleReset())
            handleErrorMsgToastVisiblity(putScheduleSessionsError.message)
        }
    }, [putScheduleSessionsLoading, putScheduleSessionsData, putScheduleSessionsError])

  useDidMountEffect(() => {
      if ([startDate, endDate, startTime, endTime].every((value) => moment.isMoment(value) && value.isValid()))
          handleOnDateTimeChange({ startDate, endDate, startTime, endTime, selectedTimeZone, setIsDateDiffLimitExceeded, setIsDateTimeValid, setDurationOfSession, isForEdit });
      else
          setIsDateTimeValid(false);
  }, [startDate, endDate, startTime, endTime]);

    const handleCloseModal = (directClick = false) => {
        if (directClick && !isEmpty(dataForSession)) storage.remove(LOCAL_STORAGE_KEYS.DATA_FOR_SESSION)
        closeModal();
    }

  const handleOnStartDateChange = (v) => {
    setStartDate(v);
    if (moment.isMoment(v) && v.isValid() && v.isAfter(endDate)) {
      setEndDate(v);
    }
  };

    const handleOnCpChange = (e) => {
        const selectedCp = ownCPList.filter((cp) => cp.collaborationProfileId == e.target.value)[0] || {}
        setSelectedColabrationProfile(selectedCp)
    }

    const handleSubmit = () => {
        let payload = {
            sessionType: 1,
            reference: sessionTitle.trim(),
            collaborationProfileId: selectedColabrationProfile.collaborationProfileId,
            from: !isEmpty(dataForSession) ? CREATE_SESSION_FROM.PARAMETERISED_URL : CREATE_SESSION_FROM.REGULAR, //// if creating session on parameterised URL, than set from value to 7 as per decision on Sasktel issue
        }

        // Adding Extra paramas
        const extraParams = filterParams(window.location.search.replace('?', ''))

        if (extraParams.length > 0 && extraParams[0] !== "") {
            let extraParamsStr = ""
            extraParamsStr = '&' + extraParams.join("&")
            if (extraParamsStr) {
                payload.queryString = extraParamsStr
            }
        }

        if (isScheduleSession) {
            const scheduleSessionPayload = generateScheduleSessionPayload({ startDate, endDate, startTime, endTime, selectedTimeZone })
            scheduleSessionPayload.assignedAgentId = hostSelectedId;
            scheduleSessionPayload.sendAgentInvite = isInviteSent;
            payload = { ...payload, ...scheduleSessionPayload }
        }

        if (isForEdit) {
            // Removing Extra Keys from Payload
            delete payload.sessionType
            delete payload.reference
            delete payload.assignedAgentId
            delete payload.sendAgentInvite
            payload.sessionId = sessionDetails.session.sessionId
            dispatch(editScheduleSession(payload))
        } else {
            dispatch(createSession(payload))
        }
    }

    const handleSessionTitleChange = (e) => {
        const val = e.target.value
        let erroMsg = false
        if (val.length > 50) {
            erroMsg = localisedValues['crt_acnt_limit_character_msg_fifty']
        }
        else if (PublicVariables.ldFlags.referenceRequired && isEmpty(val.trim())) {
            erroMsg = localisedValues['reference_required']
        }
        else if (PublicVariables.ldFlags.referenceType == 0 && !checkRegexPattern(ONLY_NUMERIC_REGEX, val.trim())) {
            erroMsg = localisedValues['numeric_characters_allowed']
        }
        setSessionTitle(val)
        setError(erroMsg)
    }

    const handleOnTimeZoneSerach = (e) => {
        const searchVal = e.target.value.toLowerCase().trim()
        const filteredData = getAllTimeZones.filter((tz) => tz.timezoneWithOffset.toLowerCase().includes(searchVal))
        setTimeZonesList(filteredData)
        clearTimeout(timeZoneAnnounceTimerRef.current)
        timeZoneAnnounceTimerRef.current = setTimeout(() => {
            announceResultCount(filteredData.length, { langId: language?.selectedlanguage?.id })
        }, 500)
    }

    const handleOnScheduleSessionSwitchChange = () => {
        setIsScheduleSession(!isScheduleSession)
        if (isScheduleSession) {
            initializeTimeZone({ setTimeZonesList, setSelectedTimeZone, getAllTimeZones, isForEdit, sessionDetails })
            initializeDateAndTimePicker({ setStartDate, setEndDate, setStartTime, setEndTime, isForEdit, sessionDetails })
            setDurationOfSession('30m')
            setIsDateTimeValid(true)
            setIsDateDiffLimitExceeded(false)
        }
    }

    const convertMinutesToExact = (minute) => {
      let updatedMintue = minute;
      if (minute > 0 && minute <= 15) {
        updatedMintue = 15;
      } else if (minute > 15 && minute <= 30) {
        updatedMintue = 30;
      } else if (minute > 30 && minute <= 45) {
        updatedMintue = 45;
      } else  {
        updatedMintue = 0;
      }
      return updatedMintue;
    };

  function handleOnTimeChnage(currentTime, isForStartTime = false) {
    let prevTimeHour, prevTimeMin
    isForStartTime ? setStartTime(currentTime) : setEndTime(currentTime);

    if (moment.isMoment(currentTime) && !['0', '15', '30', '45', 0, 15, 30, 45].includes(currentTime.minutes())) {
      let currentMin = currentTime.minutes()
      const updatedTime = currentTime.clone();
      currentMin = convertMinutesToExact(currentMin);
      updatedTime.set('minute', currentMin);
      isForStartTime ? setStartTime(updatedTime) : setEndTime(updatedTime);
      return
    }

    if (!moment.isMoment(currentTime) || !moment.isMoment(startTime) || !moment.isMoment(endTime)) return

    if (isForStartTime) {
      prevTimeHour = startTime.hours();
      prevTimeMin = startTime.minutes();
    } else {
      prevTimeHour = endTime.hours();
      prevTimeMin = endTime.minutes();
    }

    let currentstartTimeHour = currentTime.hours();
    let currentstartTimeMin = currentTime.minutes();
    currentstartTimeMin = convertMinutesToExact(currentstartTimeMin)
    let modifiedTime = currentTime;

    if (
      prevTimeHour === currentstartTimeHour &&
      prevTimeMin === 45 &&
      currentstartTimeMin === 0
    ) {
      modifiedTime = currentTime.clone().add(1, "hour"); // Create a modified copy
    } else if (
      prevTimeHour === currentstartTimeHour &&
      prevTimeMin === 0 &&
      currentstartTimeMin === 45
    ) {
      modifiedTime = currentTime.clone().subtract(1, "hour"); // Create a modified copy
    }

    isForStartTime ? setStartTime(modifiedTime) : setEndTime(modifiedTime);
  }

  const onInputKeyDown = (e) => {
    if (e.key === "Enter") {
      const nextButton = document.querySelector(".next-btn");
      if (nextButton) {
        nextButton.click();
      }
    }
  };

  const onHostSelected = (value) => {
    console.log('onHostSelected - ', value);
    setHostSelectedId(value)
  }

    return (
        <PopupModal
            closeModal={() => handleCloseModal(true)}
            modalClass="modal-medium new-session-modal"
            dialogClass="modal-dialog-centered"
            closeButton={true}
            headerContent={
                <h2 className="modal-header-step">
                    <span className="modal-header-step-top">
                        {localisedValues['step']} 1 {localisedValues['user_of']} 2 <span className="visually-hidden">, </span> <span className="next-icon icon-right-step" aria-hidden="true"></span>
                    </span>
                    <span className="modal-header-step-mid">
                        {localisedValues['enter_session_details']}
                    </span>
                </h2>
            }
            bodyContent={
                <div className="new-session-form">
                    {!isEmpty(inviteBuddyData) && <div className="invite-domain-info" data-testid='inviteBuddyDetails'>
                        <div className="invite-domain-info-logo">
                            <div className="image" style={{ backgroundImage: `url(${inviteBuddyData.USER.image})` }}></div>
                        </div>
                        <div className="invite-domain-info-text">
                            <h3>{inviteBuddyData.USER.name}</h3>
                            <p>{inviteBuddyData.USER.role}</p>
                        </div>
                    </div>}

                    {selectedContact && isEmpty(inviteBuddyData) && <div className="invite-domain-info" data-testid='selectedContactDetails'>
                        <div className="invite-domain-info-logo">
                            <div className="image contact-initials">
                                {selectedContact.name ? (selectedContact.name.trim().split(' ').length >= 2
                                    ? (selectedContact.name.trim().split(' ')[0][0] + selectedContact.name.trim().split(' ')[1][0]).toUpperCase()
                                    : selectedContact.name.substring(0, 2).toUpperCase()
                                ) : '?'}
                            </div>
                        </div>
                        <div className="invite-domain-info-text">
                            <h3>{selectedContact.name}</h3>
                            <p>{selectedContact.dialCode ? `+${selectedContact.dialCode} ` : ''}{selectedContact.phone}</p>
                        </div>
                    </div>}

                    <div className="form-group">
                        {(ownCPList.length > 1 || isForEdit) &&
                            <div className="form-group-in with-icon">
                                <label htmlFor="cpDropDownSelect" className="visually-hidden">
                                    {localisedValues['select_call_experience']}
                                </label>
                                <select id="cpDropDownSelect" disabled={isForEdit} className="form-control form-control-grey height-46 focus-font-bold" onChange={handleOnCpChange} value={selectedColabrationProfile.collaborationProfileId} title={localisedValues['select_call_experience']} data-testid='cpDropDownSelect'>
                                    {ownCPList.map((cp) => {
                                        return (
                                            <option value={cp.collaborationProfileId} key={cp.collaborationProfileId}>{replaceSpecialChar(cp.name)}</option>
                                        )
                                    })}
                                </select>
                                <span className="for-bg"></span>
                                {!isForEdit && <span className="form-group-icon bg-none">
                                    <span className="form-group-icon-in icon-arrow-down"></span>
                                </span>}
                            </div>
                        }
                    </div>
                    <div className="form-group">
                        <label htmlFor="sessionTitleInput" className="visually-hidden">
                            {localisedValues['add_title'] + '/' + localisedValues['reference']}
                        </label>
                        <div className="form-group-in">
                            <input
                                ref={referenceInputRef}
                                id="sessionTitleInput"
                                name="sessionTitle"
                                className="form-control form-control-grey height-46 focus-font-bold"
                                type="text"
                                placeholder={localisedValues['add_title'] + '/' + localisedValues['reference']}
                                data-testid='sessionTitleInput'
                                value={sessionTitle}
                                onChange={handleSessionTitleChange}
                                disabled={isForEdit || !isEmpty(dataForSession)}
                                onKeyDown={(e)=> onInputKeyDown(e)}
                                data-autofocus
                                aria-required={PublicVariables.ldFlags.referenceRequired || undefined}
                                aria-invalid={Boolean(error) || undefined}
                                aria-describedby={error ? "sessionTitleInput-error" : undefined}
                            />
                        </div>
                        {error && <div id="sessionTitleInput-error" role="alert" className="validation-error">{error}</div>}
                    </div>
                    {PublicVariables.configurations?.sessionscheduling === '1' && isEmpty(dataForSession) && <div className="schedule-session-box">
                        <div className="schedule-session-mark">
                            <div className="mark-text">
                                {localisedValues['schedule_session']}
                            </div>
                            <div className={clsx("on-off-switch collapsed", isForEdit ? 'disable-on-off-switch' : 'disable-on-off-switch1')} data-bs-toggle="collapse" data-bs-target="#schedule-session-time" style={{ pointerEvents: isForEdit ? 'none' : '' }}>
                                <label>
                                    {/* KBD (A11Y-14): styled as a switch. Native <input type="checkbox">
                                        toggles on Space but not Enter — WAI-ARIA switch pattern expects
                                        both. `role="switch"` + `aria-checked` gives the correct AT
                                        semantics (announced as "switch, on/off"), and the onKeyDown
                                        handler wires Enter to toggle. */}
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={isScheduleSession}
                                        onChange={handleOnScheduleSessionSwitchChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !isForEdit) {
                                                e.preventDefault();
                                                handleOnScheduleSessionSwitchChange();
                                            }
                                        }}
                                        data-testid='scheduleSessionSwitch'
                                        disabled={isForEdit}
                                        aria-label={localisedValues['schedule_session']}
                                    />
                                    <span className="for-design"></span>
                                </label>
                            </div>
                        </div>
                        {isScheduleSession && <div className="schedule-session-time clearfix collapse show" id="schedule-session-time">
                            <div className="schedule-session-time-in">
                                <label className="form-label font-weight-400">{localisedValues['date_time']}</label>
                                <div className="schedule-session-duration">{durationOfSession}</div>
                                <div className="session-time-row clearfix">
                                    <div className="session-time-frm clearfix">
                                        <div className="session-date">
                                            <Datetime timeFormat={false} value={startDate} onChange={(v) => handleOnStartDateChange(v)} renderInput={DatePicker} dateFormat={LONG_DATEPICKER_FORMAT} closeOnSelect={true} isValidDate={disablePrviousDate}
                                                renderDay={renderDay}
                                                renderMonth={renderMonth}
                                                renderYear={renderYear}
                                                {...datetimeOpenProps('startDate')}
                                                inputProps={{ onBlur: () => setIsDateTimeFieldFocused(true), ref: startDateRef, openPickerAriaLabel: ariaLocalised.open_date_picker, ...datetimeInputAria('startDate', ariaLocalised.start_date_label) }}
                                            />
                                        </div>
                                        <div className="session-time">
                                            <Datetime dateFormat={false} value={startTime} onChange={(v) => handleOnTimeChnage(v, true)} renderInput={Timepicker} timeFormat={TIME_FORMAT} timeConstraints={{ minutes: { step: 15 } }} {...datetimeOpenProps('startTime')} inputProps={{ onBlur: () => setIsDateTimeFieldFocused(true), ref: startTimeRef, openPickerAriaLabel: ariaLocalised.open_time_picker, ...datetimeInputAria('startTime', ariaLocalised.start_time_label) }} />
                                        </div>
                                    </div>
                                    <div className="session-time-to">
                                        {localisedValues['to_key']}
                                    </div>
                                    <div className="session-time-frm clearfix">
                                        <div className="session-date">
                                            <Datetime timeFormat={false} value={endDate} onChange={setEndDate} renderInput={DatePicker} dateFormat={LONG_DATEPICKER_FORMAT} closeOnSelect={true} isValidDate={(current) => disablePrviousDate(current, startDate)} renderDay={renderDay} {...datetimeOpenProps('endDate')} inputProps={{ onBlur: () => setIsDateTimeFieldFocused(true), ref: endDateRef, openPickerAriaLabel: ariaLocalised.open_date_picker, ...datetimeInputAria('endDate', ariaLocalised.end_date_label) }} />
                                        </div>
                                        <div className="session-time">
                                            <Datetime dateFormat={false} value={endTime} onChange={(v) => handleOnTimeChnage(v)} renderInput={Timepicker} timeFormat={TIME_FORMAT} timeConstraints={{ minutes: { step: 15 } }} {...datetimeOpenProps('endTime')} inputProps={{ onBlur: () => setIsDateTimeFieldFocused(true), ref: endTimeRef, openPickerAriaLabel: ariaLocalised.open_time_picker, ...datetimeInputAria('endTime', ariaLocalised.end_time_label) }} />
                                        </div>
                                    </div>
                                </div>
                                {isDateTimeFieldFocused && !isDateTimeValid && <div className="validation-error" id="session-datetime-error" role="alert">{localisedValues['enter_valid_start_and_end_date']}</div>}
                                {isDateTimeFieldFocused && isDateTimeValid && isDateDiffLimitExceeded && <div className="validation-error" id="session-datetime-error" role="alert">{localisedValues['schedule_session_diff_limit_error']}.</div>}

                                <div className="session-time-zone-box">
                                    <div className="session-time-zone-in">
                                        <div className="time-zone-mark" id="timeZoneLabel">
                                            {localisedValues['time_zone']}:&nbsp;
                                        </div>
                                        <div className="dropdown session-time-zone-dropdown">
                                            <button className="btn dropdown-toggle triangle-none" type="button" id="timeZoneDropdownBtn" data-bs-toggle="dropdown" aria-expanded="false" aria-labelledby="timeZoneLabel timeZoneSelectedValue" data-testid='timeZoneSelectionDiv' ref={timeZoneTriggerRef}>
                                                <span className="btn-text" id="timeZoneSelectedValue" data-testid='timeZoneSelectedValue'>{selectedTimeZone.timezoneWithOffset}</span>
                                                <span className="btn-icon icon-arrow-down" aria-hidden="true"></span>
                                            </button>
                                            <div className="dropdown-menu right" aria-labelledby="timeZoneDropdownBtn" data-testid='timeZoneListDiv' ref={timeZoneMenuRef}>
                                                <div className="dropdown-menu-in">
                                                    <div className="search-form right-icon time-zone-search">
                                                        <div className="form-group">
                                                            <label htmlFor="timeZoneSearchInput" className="visually-hidden">
                                                                {localisedValues['time_zone']}
                                                            </label>
                                                            <div className="form-group-in">
                                                                <span className="for-icon icon-search" aria-hidden="true"></span>
                                                                <input id="timeZoneSearchInput" className="form-control form-control-grey" type="text" placeholder={localisedValues['search']} onChange={handleOnTimeZoneSerach} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ul className="custom-scroll-div-thin" role="listbox" aria-label={localisedValues['time_zone']}>
                                                        {timeZonesList.length === 0 && (
                                                            <li className="time-zone-no-result" role="presentation">{localisedValues['no_user_found_lable']}</li>
                                                        )}
                                                        {timeZonesList.map((timeZone) => {
                                                            const isSelected = selectedTimeZone?.timezone === timeZone.timezone;
                                                            return (
                                                                <li key={timeZone.timezoneWithOffset} role="none"><button type="button" className="btn dropdown-item" data-testid='timeZoneItem' role="option" aria-selected={isSelected} tabIndex={-1} onClick={() => {
                                                                    setSelectedTimeZone(timeZone);
                                                                }}>{timeZone.timezoneWithOffset}{isSelected && <span className="visually-hidden">, {ariaLocalised.selected}</span>}</button></li>
                                                            )
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                        <InfoToggletip text={localisedValues['default_timezone_text']} />
                                    </div>
                                </div>
                                {!isForEdit && <div className="host-invite-email">
                                    <div className="assign-user-box clearfix">
                                        <p className="assign-p-head">
                                            <strong className="lbl">
                                                <span>{localisedValues['host']}</span>:
                                            </strong>
                                        </p>
                                        <HostDropDown localisedValues={localisedValues} onHostSelected={onHostSelected} />
                                    </div>
                                    <div className="host-invite-email-right">
                                        <div className="checkbox checkbox-border checkbox-sm">
                                            <label className="font-weight-400">
                                                <input type="checkbox" checked={isInviteSent} onChange={() => setIsInviteSent(!isInviteSent)} />
                                                <span className="for-design"></span>{localisedValues['send_email_invite_to_host']}
                                            </label>
                                        </div>
                                    </div>
                                </div>}
                            </div>
                        </div>}
                    </div>}
                    {(ownCPList?.length == 0) && 
                        <div className="validation-error schedule-session-error">
                            {localisedValues['select_any_collaboration_profiles']}
                        </div>
                    }
                </div>
            }
            disablekeyboard={true}
            backdrop='static'
            footerContent={
                <div className="button-inline d-flex">
                    <div className="button">
                        <button type="button" className="btn btn-primary btn-with-icon font-weight-500 next-btn" data-testid='nextBtn' onClick={handleSubmit} disabled={(isEmpty(ownCPList) || createSessionsLoading || putScheduleSessionsLoading || error || (PublicVariables.ldFlags.referenceRequired && isEmpty(sessionTitle) && isEmpty(dataForSession)) || (isScheduleSession && (!isDateTimeValid || isDateDiffLimitExceeded)))}>
                            {(createSessionsLoading || putScheduleSessionsLoading) && <span className="btn-loader-icon"><span className="for-icon icon-loader spin"></span></span>}
                            {isForEdit ? localisedValues['save_changes'] : localisedValues['next']} <span className="btn-next-arrow icon-right-arrow"></span></button>
                    </div>
                </div>
            }
        />
    )
}

CreateSessionModal.propTypes = {
    closeModal: PropTypes.func,
    inviteBuddyData: PropTypes.object,
    isForEdit: PropTypes.bool,
    sessionDetails: PropTypes.object,
    selectedContact: PropTypes.object,
}

export default CreateSessionModal;